// src/components/LandingPage/Navbar/NavbarDesktop.tsx
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supportedLanguages, languageFlags } from '../../../constants/languageFlags'
import logo from '../../../assets/damelio_logo_blanco.png'
import logoText from '../../../assets/damelio_texto_blanco.png'
import { useState, useRef, useEffect } from 'react'

interface NavbarDesktopProps {
  fixedTop: number
  hideScan?: boolean
}

export default function NavbarDesktop({
  fixedTop,
  hideScan = false,
}: NavbarDesktopProps) {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [showLang, setShowLang] = useState(false)
  const langRef = useRef<HTMLDivElement>(null)

  const isActive = (path: string) => location.pathname === path

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setShowLang(false)
      }
    }
    if (showLang) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showLang])

  const NavLink = (to: string, label: string) => (
    <Link
      to={to}
      className={`relative px-4 sm:px-5 py-2 sm:py-3 text-sm sm:text-base md:text-lg font-semibold transition-colors ${
        isActive(to) ? 'text-white' : 'text-gray-300 hover:text-primary group'
      }`}
    >
      {label}
      <span
        className={`absolute bottom-0 left-0 w-full h-1 bg-primary rounded-full transition-transform duration-300 ${
          isActive(to) ? '' : 'scale-x-0 group-hover:scale-x-100'
        }`}
      />
    </Link>
  )

  return (
    <nav
      className="fixed w-full z-50 bg-[#10172A] backdrop-blur-md text-white border-b border-secondary/50 shadow-md"
      style={{ top: fixedTop }}
    >
      <div className="flex justify-between items-center h-20 sm:h-24 px-4 sm:px-8 lg:px-12">
        <Link to="/" className="flex items-center space-x-3">
          <img
            src={logo}
            alt="Damelio Logo"
            className="h-14 sm:h-16 md:h-20 object-contain"
          />
          <img
            src={logoText}
            alt="Damelio Texto"
            className="h-10 sm:h-14 object-contain"
          />
        </Link>

        <div className="flex items-center space-x-4 sm:space-x-6">
          <div className="flex items-center space-x-2 sm:space-x-4">
            {NavLink('/', t('nav.home'))}
            {NavLink('/documentation', t('nav.what_we_find'))}
            {NavLink('/partners', t('nav.partners'))}
            {NavLink('/faq', t('nav.faq'))}
          </div>

          {/* solo mostramos “Scan” si hideScan es false */}
          {!hideScan && (
            <Link
              to="/scan"
              className="rounded-full bg-green-600 hover:bg-green-500 px-4 sm:px-6 py-2 sm:py-3 text-white text-sm sm:text-lg font-semibold shadow"
            >
              {t('nav.scan')}
            </Link>
          )}

          <Link
            to="/login"
            className="rounded-full bg-primary hover:bg-primary/80 px-4 sm:px-6 py-2 sm:py-3 text-white text-sm sm:text-lg font-semibold shadow"
          >
            {t('nav.login')}
          </Link>

          <div className="relative" ref={langRef}>
            <button
              onClick={() => setShowLang(!showLang)}
              className="flex items-center text-sm sm:text-lg px-4 py-2 rounded-full border border-gray-600 bg-gray-900 hover:bg-gray-800 transition-all shadow-sm"
            >
              {t('nav.language')}
              <ChevronDown className="ml-2 h-4 sm:h-5 w-4 sm:w-5" />
            </button>

            {showLang && (
              <div className="absolute right-0 mt-2 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-lg py-2 z-50">
                {supportedLanguages.map(({ code, nameKey }) => (
                  <button
                    key={code}
                    onClick={() => {
                      i18n.changeLanguage(code)
                      setShowLang(false)
                    }}
                    className={`flex items-center w-full text-left px-4 py-2 hover:bg-gray-800 transition rounded-lg ${
                      i18n.language === code ? 'font-bold text-primary' : 'text-gray-300'
                    }`}
                  >
                    <img
                      src={languageFlags[code]}
                      alt={`${code} flag`}
                      className="w-5 h-5 mr-2 rounded"
                    />
                    {t(`settings.language.${nameKey}`)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}
